/**
 * /tenants/{slug}/admins — tenant-scoped admins list route.
 *
 * Uses the tenant UUID resolved by the parent layout (see
 * routes/_authed/tenants/$slug.tsx) so the backend call goes to
 * /api/v1/tenants/{tenant_id}/admin-users with the right UUID, not
 * the slug. The backend has no cross-tenant admin list — each tenant's
 * admins are managed here, in the tenant's own scope.
 *
 * A platform admin with no console access to the tenant cannot read this
 * list, and the console layout would show only "No console access". The
 * platform can still see who administers the tenant, on its record, so
 * this route sends them there instead of to a dead end.
 */

import { AdminsTabs } from "@/features/platform-admins/components/admins-tabs";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { fetchAdmins, scopeKey } from "@/features/platform-admins/queries";
import { queryKeys } from "@/lib/query-keys";
import { sessionHasPermission, sessionQueryKey, type AdminSession } from "@/queries/session";
import { AdminsPage } from "@/features/platform-admins/pages/platform-admins-page";

function TenantAdminsPage() {
  const { tenant } = Route.useRouteContext();
  const { slug } = Route.useParams();
  return (
    <AdminsPage
      tenantID={tenant.id}
      scope="tenant"
      title="Admins"
      description={`Administrators scoped to ${tenant.name} — tenant admins, operators, and read-only roles that manage this tenant only.`}
      breadcrumbs={[{ label: tenant.name, href: `/tenants/${slug}` }, { label: "Settings" }, { label: "Admins" }]}
      tabs={<AdminsTabs tenantSlug={slug} />}
      renderInviteLink={({ children, className }) => (
        <Link
          to="/tenants/$slug/admins/invite"
          params={{ slug }}
          className={className}
        >
          {children}
        </Link>
      )}
    />
  );
}

export const Route = createFileRoute("/_authed/tenants/$slug/admins/")({
  beforeLoad: ({ context, params }) => {
    const session = context.queryClient.getQueryData<AdminSession>(sessionQueryKey);
    if (
      context.authority.access === "disabled" &&
      session?.is_platform_admin &&
      sessionHasPermission(session, "platform_admins.read")
    ) {
      throw redirect({ to: "/tenant-records/$slug/administrators", params: { slug: params.slug } });
    }
  },
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: queryKeys.adminUsers.list(scopeKey(context.tenant.id)),
      queryFn: () => fetchAdmins(context.tenant.id),
    }),
  component: TenantAdminsPage,
});
